import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  Button,
  Overflow,
  OverflowItem,
  useOverflowMenu,
} from '@fluentui/react-components';
import {
  extractExclusionRules,
  keywordsToCriterionName,
} from './helpers/utils';
import UrlsImage from './media/url.svg';
import RulesImage from './media/rules.svg';
import LabelsImage from './media/labels.svg';
import EditImage from './media/edit.svg';
import DismissImage from './media/dismiss.svg';

// eslint-disable-next-line valid-jsdoc
/**
 * There's a known bug that fluentui/react-overflow doesn't take gap of the overflow container
 * and margin of overflow item into account and cause weird styling issues.
 * https://github.com/microsoft/fluentui/issues/23066
 */
const getStyles = () => ({
  previewContainer: {
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(255, 255, 255, 1)',
    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.1)',
    padding: '12px',
    margin: '4px 28px',
    borderRadius: '4px',
    gap: '4px',
    maxWidth: '95%',
  },
  previewHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    '& .button-group': {
      display: 'flex',
      flexDirection: 'row',
      gap: '12px',
      '& button': {
        padding: '0px',
        minWidth: '20px',
      },
    },
  },
  previewRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '4px',
    flexWrap: 'nowrap',
    maxWidth: '95%',
    '& .row-label': {
      padding: '2px 0px 2px 0px',
      textWrap: 'nowrap',
    },
  },
  overflowContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    flexGrow: 1,
    minWidth: 0,
    maxWidth: '100%',
    overflowX: 'hidden',
    gap: '4px',
    '& .tag': {
      padding: '2px 8px 2px 8px',
      borderRadius: '4px',
      background: 'rgba(243, 242, 241, 1)',
      flexShrink: 0,
      maxWidth: '250px',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflowX: 'hidden',
    },
  },
});

const OverflowPlaceholder = () => {
  const { ref, isOverflowing } = useOverflowMenu();
  if (!isOverflowing) {
    return null;
  }
  return (<span ref={ref} className="tag">...</span>);
};

const UnStyledFinalUrlExpansionPreview = ({
  classes,
  i18n,
  finalUrlExpansions,
  setToggleFinalURLsEditor,
  onChange,
}) => {
  const [urls, rules, labels] = useMemo(() => extractExclusionRules(finalUrlExpansions), [finalUrlExpansions]);

  const onEditClick = useCallback(() => setToggleFinalURLsEditor(true), [setToggleFinalURLsEditor]);
  const onDismissClick = useCallback(() => {
    onChange({ finalUrlExpansions: [] });
  }, [onChange]);

  const renderUrlsRow = useMemo(() => (
    <div className={classes.previewRow}>
      { UrlsImage && <img src={UrlsImage} alt="Link regular icon" /> }
      <span className="row-label">{urls.length} {i18n.getString(_TL_('URLs'))}: </span>
      <Overflow>
        <div className={classes.overflowContainer}>
          { urls.map(url => (
            <OverflowItem key={url} id={url}>
              <span className={classnames('auto-generated-assets-preview-url-tag', 'tag')}>
                {url}
              </span>
            </OverflowItem>
          )) }
          <OverflowPlaceholder />
        </div>
      </Overflow>
    </div>
  ), [classes, i18n, urls]);

  const renderRulesRow = useMemo(() => (
    <div className={classes.previewRow}>
      { RulesImage && <img src={RulesImage} alt="Link edit regular icon" /> }
      <span className="row-label">{rules.length} {i18n.getString(_TL_('Exclusion Rules'))}: </span>
      <Overflow>
        <div className={classes.overflowContainer}>
          { rules.map((rule) => {
            const tag = keywordsToCriterionName(rule);
            return (
              <OverflowItem key={tag} id={tag}>
                <span className={classnames('auto-generated-assets-preview-rule-tag', 'tag')}>
                  {tag}
                </span>
              </OverflowItem>
            );
          }) }
          <OverflowPlaceholder />
        </div>
      </Overflow>
    </div>
  ), [classes, i18n, rules]);

  const renderLabelsRow = useMemo(() => (
    <div className={classes.previewRow}>
      { LabelsImage && <img src={LabelsImage} alt="Label regular icon" /> }
      <span className="row-label">{labels.length} {i18n.getString(_TL_('Custom Labels'))}: </span>
      <Overflow>
        <div className={classes.overflowContainer}>
          { labels.map(label => (
            <OverflowItem key={label} id={label}>
              <span className={classnames('auto-generated-assets-preview-label-tag', 'tag')}>
                {label}
              </span>
            </OverflowItem>
          )) }
          <OverflowPlaceholder />
        </div>
      </Overflow>
    </div>
  ), [classes, i18n, labels]);

  return (
    <div className={classes.previewContainer}>
      <div className={classes.previewHeader}>
        <span><b>{ i18n.getString((_TL_('Excluded some URLs'))) }</b></span>
        <div className="button-group">
          <Button className="auto-generated-assets-preview-edit-btn" appearance="transparent" onClick={onEditClick}><img src={EditImage} alt="Edit icon" /></Button>
          <Button className="auto-generated-assets-preview-remove-btn" appearance="transparent" onClick={onDismissClick}><img src={DismissImage} alt="Dismiss icon" /></Button>
        </div>
      </div>
      { urls.length > 0 && renderUrlsRow }
      { rules.length > 0 && renderRulesRow }
      { labels.length > 0 && renderLabelsRow }
    </div>
  );
};

UnStyledFinalUrlExpansionPreview.propTypes = {
  classes: PropTypes.shape({
    previewContainer: PropTypes.string,
    previewHeader: PropTypes.string,
    previewRow: PropTypes.string,
    overflowContainer: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  finalUrlExpansions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setToggleFinalURLsEditor: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export const FinalUrlExpansionPreview = withDefaultStyles(UnStyledFinalUrlExpansionPreview, getStyles);
